import fs from 'fs';
import path from 'path';
import { logger } from '../utils/logger';
import { PROMPTS, DATABASE_RULES } from './prompts';

export interface PromptLoaderConfig {
	promptsDir?: string;
}

interface CachedPromptTemplate {
	system: string;
	user: string;
}

/**
 * PromptLoader class for loading and processing prompt templates
 * Tries to load from file system first, then falls back to hardcoded prompts
 */
export class PromptLoader {
	private promptsDir: string;
	private promptCache: Map<string, CachedPromptTemplate> = new Map();
	private databaseRulesCache: Map<string, string> = new Map();
	private isInitialized: boolean = false;
	private databaseType: string = 'postgresql';

	constructor(config?: PromptLoaderConfig) {
		// Default to backend's .prompts directory (where the SDK is being used)
		this.promptsDir = config?.promptsDir || path.join(process.cwd(), '.prompts');
	}

	/**
	 * Load a prompt template from file system OR fallback to hardcoded prompts
	 * @param promptName - Name of the prompt folder
	 * @returns Template with system and user prompts
	 */
	private loadPromptTemplate(promptName: string): CachedPromptTemplate {
		// First, try to load from file system (backend's .prompts directory)
		try {
			const systemPath = path.join(this.promptsDir, promptName, 'system.md');
			const userPath = path.join(this.promptsDir, promptName, 'user.md');

			if (fs.existsSync(systemPath) && fs.existsSync(userPath)) {
				const system = fs.readFileSync(systemPath, 'utf-8');
				const user = fs.readFileSync(userPath, 'utf-8');
				return { system, user };
			}
		} catch (error) {
			// Ignore file system errors and try fallback
			logger.error(`Could not load '${promptName}' from file system, trying fallback...`);
		}

		// Fallback to hardcoded prompts
		const hardcodedPrompt = PROMPTS[promptName];
		if (hardcodedPrompt) {
			return hardcodedPrompt;
		}

		throw new Error(`Prompt template '${promptName}' not found in either ${this.promptsDir} or hardcoded prompts. Available prompts: ${Object.keys(PROMPTS).join(', ')}`);
	}

	/**
	 * Initialize and cache all prompts into memory
	 * Tries file system first, then falls back to hardcoded prompts
	 */
	async initialize(): Promise<void> {
		if (this.isInitialized) {
			logger.debug('PromptLoader already initialized, skipping...');
			return;
		}


		const promptTypes = Object.keys(PROMPTS);

		for (const promptName of promptTypes) {
			try {
				const template = this.loadPromptTemplate(promptName);
				this.promptCache.set(promptName, template);
			} catch (error) {
				logger.error(`Failed to load prompt '${promptName}':`, error);
				throw error;
			}
		}

		this.isInitialized = true;
	}

	/**
	 * Replace variables in a template string using {{VARIABLE_NAME}} pattern
	 * @param template - Template string with placeholders
	 * @param variables - Variables to replace in the template
	 * @returns Processed string
	 */
	private replaceVariables(
		template: string,
		variables: Record<string, string | number | boolean | any[]>
	): string {
		// Single-pass replacement of every {{VARIABLE_NAME}} placeholder.
		//
		// Using a REPLACER FUNCTION is critical: String.replace() interprets `$`
		// sequences ($&, $', $`, $1, $$) in a replacement *string*, but inserts a
		// function's return value VERBATIM. Injected values routinely contain `$`
		// (e.g. DuckDB regex anchors like '(\S+)$' in KB recipe nodes); the old
		// per-key string replace mangled those — splicing later sections of the
		// prompt into the middle of the value and destroying the content.
		//
		// A single pass (vs a loop of per-key replaces) also prevents value-injection:
		// the regex scans the ORIGINAL template once, so a value that happens to
		// contain `{{OTHER_KEY}}` is never re-substituted, and an unknown placeholder
		// is left intact rather than blanked.
		return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
			if (!(key in variables)) return match;
			const value = variables[key];
			return typeof value === 'string' ? value : JSON.stringify(value);
		});
	}

	/**
	 * Load both system and user prompts from cache and replace variables
	 * Supports prompt caching by splitting static and dynamic content
	 * @param promptName - Name of the prompt
	 * @param variables - Variables to replace in the templates
	 * @returns Object containing both system and user prompts (system can be string or array for caching)
	 */
	async loadPrompts(
		promptName: string,
		variables: Record<string, string | number | boolean | any[]>
	): Promise<{ system: string | any[]; user: string }> {
		if (!this.isInitialized) {
			logger.warn('PromptLoader not initialized, initializing now...');
			await this.initialize();
		}

		const template = this.promptCache.get(promptName);

		if (!template) {
			throw new Error(`Prompt template '${promptName}' not found in cache. Available prompts: ${Array.from(this.promptCache.keys()).join(', ')}`);
		}

		// Check if the prompt has a context section marker (for caching)
		const contextMarker = '---\n\n## CONTEXT';

		if (template.system.includes(contextMarker)) {
			// Split into static (cacheable) and dynamic (context) sections
			const [staticPart, contextPart] = template.system.split(contextMarker);

			// Replace variables in BOTH parts
			// Static part may contain schema/rules which are per-project but stable
			const processedStatic = this.replaceVariables(staticPart, variables);
			const processedContext = this.replaceVariables(contextMarker + contextPart, variables);

			// Return as array of blocks for Anthropic caching
			// The static part (with schema) will be cached after first request
			return {
				system: [
					{
						type: 'text',
						text: processedStatic.trim(),
						cache_control: { type: 'ephemeral' }
					},
					{
						type: 'text',
						text: processedContext.trim()
					}
				],
				user: this.replaceVariables(template.user, variables)
			};
		}

		// Fallback: No caching, return as simple string
		return {
			system: this.replaceVariables(template.system, variables),
			user: this.replaceVariables(template.user, variables)
		};
	}

	/**
	 * DEPRECATED: Use loadPrompts instead
	 * Load a single prompt file and replace variables using {{VARIABLE_NAME}} pattern
	 */
	async loadPrompt(
		promptName: string,
		promptType: 'system' | 'user',
		variables: Record<string, string | number | boolean | any[]>
	): Promise<string | any[]> {
		const prompts = await this.loadPrompts(promptName, variables);
		return promptType === 'system' ? prompts.system : prompts.user;
	}

	/**
	 * Set custom prompts directory (requires re-initialization)
	 * @param dir - Path to the prompts directory
	 */
	setPromptsDir(dir: string): void {
		this.promptsDir = dir;
		this.isInitialized = false;
		this.promptCache.clear();
	}

	/**
	 * Get current prompts directory
	 * @returns Path to the prompts directory
	 */
	getPromptsDir(): string {
		return this.promptsDir;
	}

	/**
	 * Check if prompts are loaded in memory
	 */
	isReady(): boolean {
		return this.isInitialized;
	}

	/**
	 * Get the number of cached prompts
	 */
	getCacheSize(): number {
		return this.promptCache.size;
	}

	/**
	 * Set the database type for SQL rules loading
	 * @param type - Database type ('postgresql' | 'mssql')
	 */
	setDatabaseType(type: string): void {
		this.databaseType = type;
		this.databaseRulesCache.clear();
	}

	/**
	 * Get current database type
	 * @returns Database type string
	 */
	getDatabaseType(): string {
		return this.databaseType;
	}

	/**
	 * Load database-specific SQL rules from file system
	 * Falls back to minimal default rules if file not found
	 * @returns Database rules as a string
	 */
	async loadDatabaseRules(): Promise<string> {
		// Check cache first
		if (this.databaseRulesCache.has(this.databaseType)) {
			return this.databaseRulesCache.get(this.databaseType)!;
		}

		// Try loading from file system
		const rulesPath = path.join(this.promptsDir, 'database-rules', `${this.databaseType}.md`);

		try {
			if (fs.existsSync(rulesPath)) {
				const rules = fs.readFileSync(rulesPath, 'utf-8');
				this.databaseRulesCache.set(this.databaseType, rules);
				return rules;
			}
		} catch (error) {
			logger.warn(`Could not load database rules for '${this.databaseType}' from file system: ${error}`);
		}

		// Fallback to default rules
		const defaultRules = this.getDefaultDatabaseRules();
		this.databaseRulesCache.set(this.databaseType, defaultRules);
		logger.warn(`Using default database rules for '${this.databaseType}' (file not found at ${rulesPath})`);
		return defaultRules;
	}

	/**
	 * Load database-specific SQL rules for a given source type.
	 * Used by the agent architecture where multiple source types can coexist.
	 * @param sourceType - Source type from tool ID (e.g., 'postgres', 'mssql', 'mysql')
	 * @returns Database rules as a string
	 */
	async loadDatabaseRulesForType(sourceType: string): Promise<string> {
		// Map source types to database rule file names
		const typeMap: Record<string, string> = {
			'postgres': 'postgresql',
			'postgresql': 'postgresql',
			'mssql': 'mssql',
			'mysql': 'postgresql', // MySQL uses similar rules to PostgreSQL
			'excel': 'duckdb', // Excel sources use DuckDB engine
			'csv': 'duckdb', // CSV sources use DuckDB engine
			'dynamodb': 'dynamodb', // DynamoDB uses its own PartiQL rules (no JOIN/GROUP BY/aggregates)
			'redis': 'redis', // Redis uses its own command rules (key/pattern lookups, no SQL)
		};
		const dbType = typeMap[sourceType] || 'postgresql';

		// Check cache first
		if (this.databaseRulesCache.has(dbType)) {
			return this.databaseRulesCache.get(dbType)!;
		}

		// Try loading from file system
		const rulesPath = path.join(this.promptsDir, 'database-rules', `${dbType}.md`);

		try {
			if (fs.existsSync(rulesPath)) {
				const rules = fs.readFileSync(rulesPath, 'utf-8');
				this.databaseRulesCache.set(dbType, rules);
				return rules;
			}
		} catch (error) {
			logger.warn(`Could not load database rules for '${dbType}' from file system: ${error}`);
		}

		// Fallback to default rules
		const defaultRules = this.getDefaultDatabaseRulesForType(dbType);
		this.databaseRulesCache.set(dbType, defaultRules);
		return defaultRules;
	}

	/**
	 * Get default database rules as fallback (mirror of
	 * backend/.prompts/database-rules/<engine>.md, embedded via DATABASE_RULES).
	 * @returns Database rules for the configured databaseType
	 */
	private getDefaultDatabaseRules(): string {
		return DATABASE_RULES[this.databaseType] ?? DATABASE_RULES['postgresql'];
	}

	/**
	 * Get default database rules for a specific engine type (mirror of
	 * backend/.prompts/database-rules/<engine>.md, embedded via DATABASE_RULES).
	 */
	private getDefaultDatabaseRulesForType(dbType: string): string {
		return DATABASE_RULES[dbType] ?? DATABASE_RULES['postgresql'];
	}
}

// Export a singleton instance
// Default to backend's .prompts directory (where the SDK is being used)
// If prompts are not found there, the loader will fallback to hardcoded prompts
const defaultPromptsPath = process.env.PROMPTS_DIR || path.join(process.cwd(), '.prompts');

export const promptLoader = new PromptLoader({
	promptsDir: defaultPromptsPath
});
